/**
 * THE BUY BUTTONS — fetched at RUNTIME from the backend, not baked in at build time (2026-09-20).
 *
 * WHY THIS FILE EXISTS. The Payment Link URLs are CONFIG, and their home is the config kernel
 * (`raisy_BE/cfg/raisy_subscription_plans.json` → `stripe.payment_link_ids`). They were carried in the
 * frontend as build-time env (`VITE_STRIPE_*`), which made every newly minted link a code edit plus a
 * frontend rebuild. That is the wrong dependency for a buy button in both directions:
 *
 *   - a link that exists in Stripe but not in a deployed bundle is a customer who cannot pay;
 *   - and a bundle that cannot be redeployed makes a config change wait on a build. Measured on
 *     2026-09-20: a push touching `raisy_FE/` left the DEV bundle's hash unchanged for 40 minutes.
 *
 * The build-time value stays as the FALLBACK, so nothing regresses if the request fails: the dialog
 * renders whatever it has and says honestly that links are still being set up.
 */
import { useEffect, useState } from "react";

import { STRIPE_CREDITS, STRIPE_GROWTH, STRIPE_SCALE, STRIPE_STARTER } from "./plans";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

export type StripeLinkKey = "starter" | "growth" | "scale" | "buy_credits";

export type StripeLinks = Record<StripeLinkKey, string>;

/** What the last successful build baked in. Used until (or unless) the kernel answers. */
const BUILD_TIME_FALLBACK: StripeLinks = {
  starter: STRIPE_STARTER,
  growth: STRIPE_GROWTH,
  scale: STRIPE_SCALE,
  buy_credits: STRIPE_CREDITS,
};

const KEYS = Object.keys(BUILD_TIME_FALLBACK) as StripeLinkKey[];

/** How many times a failed read is retried, and how far apart — the reason is in the effect below. */
const ATTEMPTS = 4;
const RETRY_MS = 3000;

export const useStripeLinks = (): StripeLinks => {
  const [links, setLinks] = useState<StripeLinks>(BUILD_TIME_FALLBACK);

  useEffect(() => {
    let alive = true;

    /**
     * ONE FAILED FETCH MUST NOT TELL A CUSTOMER WE HAVE NO PAYMENT PAGE.
     *
     * The DEV backend answers 502 for the few seconds a rebuild takes, and the DEV frontend is rebuilt
     * BY THE SAME PUSH — so the two are briefly down together and the read lands in that window. The PO
     * hit exactly that (2026-09-21): *"buy credits 1 USD is already available why 'checkout link coming
     * shortly' ? its already there !"* The link WAS there — the kernel carries all four — it had simply
     * not been read yet, and the build-time fallback is empty because the links' home moved INTO the
     * kernel precisely so they would stop being baked into a bundle.
     *
     * So: a few attempts, spaced. The fallback stands only when every one of them fails, which keeps a
     * few seconds of an ordinary deploy from being visible as a product that does not exist.
     */
    const load = async (attempt: number): Promise<void> => {
      try {
        const res = await fetch(`${API_URL}/api/v1/subscription/payment-links`);
        if (!res.ok) throw new Error(`payment-links answered ${res.status}`);
        const body = await res.json();
        if (!alive) return;
        const fromKernel = body?.data?.links ?? {};
        setLinks((previous) => {
          const next = { ...previous };
          KEYS.forEach((key) => {
            const url = fromKernel[key]?.url;
            // Only a real URL overrides the fallback: an omitted key means "not minted yet", which must
            // not wipe a link that a previous build happens to know about.
            if (typeof url === "string" && url) next[key] = url;
          });
          return next;
        });
      } catch {
        if (!alive) return;
        if (attempt < ATTEMPTS) {
          window.setTimeout(() => void load(attempt + 1), RETRY_MS);
          return;
        }
        // Every attempt failed. The build-time fallback stands — it never hides a working button.
      }
    };
    void load(1);

    return () => {
      alive = false;
    };
  }, []);

  return links;
};
